// Centralized API Client for ReUseIt Super Admin Portal

const API_BASE = (import.meta as any).env?.VITE_API_BASE_URL || 'https://api.reuseit.shop/v1';

export class AdminApiClient {
  private static getToken(): string | null {
    return localStorage.getItem('reuseit_admin_jwt') || localStorage.getItem('token');
  }

  private static async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const res = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${res.status} ${res.statusText}`);
    }

    return res.json();
  }

  // ── Overview & Dashboard ─────────────────────────────────
  static async getOverview() {
    return this.request<{ stats: any }>('/admin/overview');
  }

  // ── Users & KYC Management ───────────────────────────────
  static async getUsers() {
    return this.request<any[]>('/admin/users');
  }

  static async updateUser(id: string, data: { role?: string; kycStatus?: string; isSeller?: boolean }) {
    return this.request<{ success: boolean; user: any }>(`/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  static async updateKyc(userId: string, status: 'VERIFIED' | 'REJECTED') {
    return this.request<{ success: boolean; user: any }>(`/admin/users/${userId}/kyc`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  // ── Listings Moderation ──────────────────────────────────
  static async getListings(status?: string) {
    const q = status ? `?status=${status}` : '';
    return this.request<any[]>(`/admin/listings${q}`);
  }

  static async moderateListing(id: string, status: 'ACTIVE' | 'FLAGGED' | 'DELISTED', notes?: string) {
    return this.request<{ success: boolean; listing: any }>(`/admin/listings/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, notes }),
    });
  }

  // ── Dispute Tribunal ─────────────────────────────────────
  static async getDisputes() {
    return this.request<any[]>('/admin/disputes');
  }

  static async resolveDispute(id: string, action: 'RELEASE_TO_SELLER' | 'REFUND_TO_BUYER', notes?: string) {
    return this.request<{ success: boolean; message: string }>(`/admin/disputes/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ action, notes }),
    });
  }

  // ── Broadcast Push Notifications ─────────────────────────
  static async broadcastPush(data: { title: string; message: string; type?: string; actionUrl?: string }) {
    return this.request<{ success: boolean; broadcast: any }>('/admin/broadcast', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async getBroadcasts() {
    return this.request<any[]>('/admin/broadcasts');
  }

  // ── CMS Articles & Dynamic Policy Pages ──────────────────
  static async getArticles() {
    return this.request<any[]>('/admin/cms/articles');
  }

  static async saveArticle(data: { id?: string; slug: string; title: string; category: string; content: string; authorName?: string; isPublished?: boolean }) {
    return this.request<{ success: boolean; article: any }>('/admin/cms/articles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async deleteArticle(id: string) {
    return this.request<{ success: boolean }>(`/admin/cms/articles/${id}`, {
      method: 'DELETE',
    });
  }

  // ── CMS Banners & Promotional Carousels ──────────────────
  static async getAllBanners() {
    return this.request<any[]>('/admin/config/banners');
  }

  static async upsertBanner(banner: any) {
    return this.request<{ success: boolean; banner: any }>('/admin/config/banners', {
      method: 'POST',
      body: JSON.stringify(banner),
    });
  }

  static async deleteBanner(id: string) {
    return this.request<{ success: boolean }>('/admin/config/banners/' + id, {
      method: 'DELETE',
    });
  }

  // ── Platform Settings & CMS Blocks ───────────────────────
  static async updateSettings(key: string, value: any) {
    return this.request<{ success: boolean; setting: any }>('/admin/config/settings', {
      method: 'PUT',
      body: JSON.stringify({ key, value }),
    });
  }

  static async getPublicConfig() {
    return this.request<any>('/config/public');
  }
}
